import moment from "moment";
import currency from "currency.js";
import { Engine } from "json-rules-engine";
import {
  CATEGORIES,
  ITEM_PROGRESS_STEPS,
  ORDER_PROGRESS_STEPS,
  PROGRESS_LANES,
} from "./constants";
import { resolveVariantOptionOverrides } from "./variantSizeOverride";

export const formatPhoneNumber = (value) => {
  // if input value is falsy eg if the user deletes the input, then just return
  if (!value) return value;

  // clean the input for any non-digit values.
  const phoneNumber = value.replace(/[^\d]/g, "");

  // phoneNumberLength is used to know when to apply our formatting for the phone number
  const phoneNumberLength = phoneNumber.length;

  var match = phoneNumber.match(/^(\d{1,3}|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    var intlCode = match[1] ? `+${match[1]} ` : "";
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
  } else {
    return value;
  }
};

export function getQueryParam(queryStr, p) {
  const arr = queryStr.replace("?", "").split("&");
  for (const a of arr) {
    if (a.startsWith(`${p}=`)) {
      return decodeURIComponent(a.substring(p.length + 1));
    }
  }
  return undefined;
}

export function capitalize(s) {
  if (typeof s !== "string") return "";

  const words = s.split(" ");
  for (let i = 0; i < words.length; i++) {
    if (words[i]) {
      words[i] = words[i][0].toUpperCase() + words[i].substr(1);
    }
  }
  return words.join(" ");
}

export function createSnippets(productType, shape, size, servings) {
  const data = [];
  if (productType === "Cake") {
    if (shape && size) {
      data.push(`${shape}, ${size} cake`);
    }
    if (servings) {
      data.push(`${servings} servings`);
    }
  } else {
    if (productType && servings) {
      data.push(`${servings} ${productType}`);
    }
  }

  return data;
}

export function isV2SaaSPlan(plan) {
  return plan === "starter" || plan === "professional" || plan === "premium";
}

export function getDisplayName(firstName, lastName, name) {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  } else if (firstName) {
    return firstName;
  } else {
    return name;
  }
}

export function getDefaultQuoteExpiration(eventDate) {
  if (eventDate) {
    const daysToEvent = moment(eventDate).diff(moment(), "days");
    if (daysToEvent < 3) {
      return moment().add(4, "hours");
    } else if (daysToEvent < 5) {
      return moment().add(1, "days");
    } else if (daysToEvent < 8) {
      return moment().add(2, "days");
    } else if (daysToEvent < 15) {
      return moment().add(3, "days");
    } else if (daysToEvent < 22) {
      return moment().add(4, "days");
    } else if (daysToEvent <= 30) {
      return moment().add(5, "days");
    } else {
      return moment().add(7, "days");
    }
  } else {
    return moment().add(1, "days");
  }
}

export function getDefaultQuoteExtension(eventDate) {
  const daysToEvent = moment(eventDate).diff(moment(), "days");
  console.log("daysToEvent", daysToEvent);

  const exdate = moment();
  if (daysToEvent < 3) {
    exdate.add(2, "hours");
  } else if (daysToEvent < 8) {
    exdate.add(12, "hours");
  } else {
    exdate.add(24, "hours");
  }

  return exdate.format();
}

/**
 * Default balance-due date (YYYY-MM-DD), relative to fulfillment date:
 * >7 days out → 7 days before event; >3 days → 2 days before; <1 day → event day; else 1 day before.
 */
export function getDefaultBalanceDueDate(fulfillmentDateStr) {
  if (!fulfillmentDateStr) return "";
  const daysToDueDate = moment(fulfillmentDateStr).diff(moment(), "days", true);
  let defaultDaysBefore = 0;
  if (daysToDueDate > 7) {
    defaultDaysBefore = 7;
  } else if (daysToDueDate > 3) {
    defaultDaysBefore = 2;
  } else if (daysToDueDate < 1) {
    defaultDaysBefore = 0;
  } else {
    defaultDaysBefore = 1;
  }
  return moment(fulfillmentDateStr, "YYYY-MM-DD")
    .subtract(defaultDaysBefore, "days")
    .format("YYYY-MM-DD");
}

export function isPWA() {
  return window.navigator.standalone || window.matchMedia("(display-mode: standalone)").matches;
}

export function getOS() {
  var userAgent = window.navigator.userAgent,
    platform = window.navigator?.userAgentData?.platform || window.navigator.platform,
    macosPlatforms = ["Macintosh", "MacIntel", "MacPPC", "Mac68K"],
    windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"],
    iosPlatforms = ["iPhone", "iPad", "iPod"],
    os = null;

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = "Mac OS";
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = "iOS";
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = "Windows";
  } else if (/Android/.test(userAgent)) {
    os = "Android";
  } else if (/Linux/.test(platform)) {
    os = "Linux";
  }

  return os;
}

export function generateServingsSnippet(servings) {
  if (servings.length < 7) {
    return servings + " servings";
  } else {
    return servings;
  }
}

export function getAmountWithTxnFee(amount, fees = { net: "0.045" }) {
  return currency(amount).add(currency(amount).multiply(fees.net)).toString();
}

export function getAmountWithoutTxnFee(amount, fees = { net: "0.045" }) {
  return currency(amount)
    .divide(1 + +fees.net)
    .toString();
}

export function getOriginalAmount(amount, includeFees, fees) {
  return (includeFees ? getAmountWithoutTxnFee(amount, fees) : amount) || "";
}

export function getApplicationFee(
  amount,
  fees = { net: "0.045", payment: "0.035", success: "0.01", flat: "0" }
) {
  const feeFactor = +fees.payment + +fees.success;
  return currency(amount).multiply(feeFactor).add(fees.flat).toString();
}

/** True when baker uses cost+ fee pricing (`user.config.fees.model`, e.g. ProductEntry). */
export function isCostPlusFeesModel(fees) {
  return fees?.model === "cost+";
}

export function isPOSBakery(account) {
  return (
    account.capabilities &&
    account.capabilities.includes("pos") &&
    isCostPlusFeesModel(account.config.fees)
  );
}

/**
 * Cash discount to strip card-fee markup for cost+ pricing.
 * When a line total was built with getAmountWithTxnFee(base), total = base × (1 + net).
 * The fee portion of that gross total is total × net/(1+net), not total × net.
 * Only applies to lines with includeFees (lines without fee markup contribute 0).
 */
export function getCashPaymentDiscountTotal(items, fees) {
  if (!fees || !isCostPlusFeesModel(fees) || fees.net == null || +fees.net <= 0) {
    return 0;
  }
  const net = +fees.net;
  const feeShareOfGrossTotal = net / (1 + net);
  return (items || []).reduce((sum, item) => {
    return sum.add(currency(item.total || 0).multiply(feeShareOfGrossTotal));
  }, currency(0)).value;
}

/**
 * Payment object for APIs that persist orders/charges. Omits UI-only `cashDiscount`.
 * When `combineCashIntoDiscount` is true (cash orders), sets `discount` to merchant + cash.
 */
export function buildPaymentForOrderApi(payment, dueDate) {
  const p = payment && typeof payment === "object" ? payment : {};
  const { cashDiscount, ...rest } = p;
  const out = { ...rest, dueDate };
  if (+cashDiscount > 0) {
    out.discount = currency(p.discount || 0).add(cashDiscount || 0).value;
  }
  return out;
}

/** Stored on order.payment.method and transaction pmCode for POS offline orders. */
export const OFFLINE_PAYMENT_METHOD_LABELS = {
  cash: "Cash",
  check: "Check",
  gift_card: "Gift Card",
  standalone_terminal: "Standalone Terminal",
};

/** POS UI key for check / gift card / standalone terminal (not KitchenHub paymentType external). */
export const POS_OTHER_PAYMENT_UI_KEY = "other";

export const OTHER_POS_PAYMENT_METHODS = {
  check: { title: "Check", label: "CHECK" },
  gift_card: { title: "Gift Card", label: "GIFT CARD" },
  standalone_terminal: { title: "Standalone Terminal", label: "STANDALONE TERMINAL" },
};

/**
 * "Other" POS payment types shown at checkout. Bakery-level override via
 * config.paymentMethods, e.g. { ebt: { title: "EBT", cashDiscount: true }, check: { title: "Check" } }.
 * When present it replaces OTHER_POS_PAYMENT_METHODS; otherwise the defaults are used.
 * `cashDiscount: true` gives that method the cash discount. A `cash` entry is skipped here
 * (Cash has its own tile) but its `cashDiscount` flag is honored by isCashDiscountPaymentMethod.
 */
export function getOtherPosPaymentMethods(config) {
  const custom = config?.paymentMethods;
  if (!custom || typeof custom !== "object") {
    return OTHER_POS_PAYMENT_METHODS;
  }
  const methods = {};
  for (const [key, value] of Object.entries(custom)) {
    if (key === "cash" || !value || typeof value !== "object") {
      continue;
    }
    const title = String(value.title || OTHER_POS_PAYMENT_METHODS[key]?.title || key).trim();
    methods[key] = {
      title,
      label: value.label || title.toUpperCase(),
      cashDiscount: value.cashDiscount === true,
    };
  }
  return Object.keys(methods).length > 0 ? methods : OTHER_POS_PAYMENT_METHODS;
}

/** Cash qualifies unless config.paymentMethods.cash.cashDiscount is false; others only when flagged. */
export function isCashDiscountPaymentMethod(paymentMethod, config) {
  if (!paymentMethod) {
    return false;
  }
  if (paymentMethod === "cash") {
    return config?.paymentMethods?.cash?.cashDiscount !== false;
  }
  return getOtherPosPaymentMethods(config)[paymentMethod]?.cashDiscount === true;
}

export function getOfflinePaymentMethodLabel(method, { receipt = false } = {}) {
  const key = String(method || "cash").toLowerCase();
  if (receipt) {
    const receiptLabels = {
      cash: "CASH",
      check: "CHECK",
      gift_card: "GIFT CARD",
      standalone_terminal: "TERMINAL",
    };
    return receiptLabels[key] || "OFFLINE PAYMENT";
  }
  return OFFLINE_PAYMENT_METHOD_LABELS[key] || "Offline payment";
}

export function getDataFromLocalStorage(key, ttl) {
  const value = localStorage.getItem(key);

  if (value) {
    // parse to JSON
    const data = JSON.parse(value);
    if (ttl && data.ts) {
      const cacheAge = Date.now() - +data.ts;
      if (cacheAge > +ttl) {
        localStorage.removeItem(key);
        return "";
      }
    }

    return data;
  }

  return "";
}

export function setDataInLocalStorage(key, object) {
  localStorage.setItem(key, JSON.stringify({ ...object, ts: Date.now() }));
}

export function getCachedState(key, maxCacheAge) {
  let cache = "";
  const landing = getDataFromLocalStorage("landing");
  // is this the same page that was refreshed?
  if (landing && landing.page === window.location.pathname) {
    const localData = getDataFromLocalStorage(key);
    cache = localData;

    if (maxCacheAge) {
      const cacheAge = Date.now - +localData.ts;
      if (cacheAge > +maxCacheAge) {
        cache = "";
      }
    }
  }
  return cache;
}

export function getCustomerName(customer) {
  if (!customer) {
    return "";
  } else if (customer.firstName) {
    return `${customer.firstName} ${customer.lastName || ""}`;
  } else if (customer.name && customer.lastName) {
    return `${customer.name} ${customer.lastName}`;
  } else {
    return customer.name || "";
  }
}

export function getCustomerFirstNameLastInitial(name) {
  // Create an array containing each of the words in the name
  var names = name.split(/\s+/);

  if (names.length > 1) {
    // Replaces the first name with an initial, followed by a period.
    names[1] = names[1].substr(0, 1) + ".";
  }

  // Glue the pieces back together.
  return names.join(" ");
}

export function getStartTime(str) {
  let hour = 0;
  const time = (str?.trim() || "").toLowerCase();

  const amIdx = time.indexOf("am");
  const noonIdx = time.indexOf("noon");
  const pmIdx = time.indexOf("pm");

  // find first occurence
  const arr = [amIdx, noonIdx, pmIdx].filter((idx) => idx >= 0);
  if (arr.length > 0) {
    // split time string at the first match
    const splitIdx = Math.min(...arr);
    if (noonIdx === splitIdx) {
      hour = 12;
    } else {
      // split string and extract hour
      hour = parseInt(time.substring(0, splitIdx));
      if (pmIdx === splitIdx) {
        if (hour !== 12) {
          hour = hour + 12;
        }
      }
    }
  }

  return hour;
}

/**
 * Formats `fulfillment.time` (e.g. "3pm" or "3pm - 4pm") for display.
 * Uses {@link getStartTime} on the start of the slot, then renders as "7.00pm".
 * @param {string} [timeStr] - Raw fulfillment time string
 * @returns {string} Formatted time or "" when missing/invalid
 */
export function formatFulfillmentTime(timeStr) {
  const raw = (timeStr && String(timeStr).trim()) || "";
  if (!raw) {
    return "";
  }
  const hour24 = getStartTime(raw);
  if (!Number.isFinite(hour24) || hour24 < 0 || hour24 > 23) {
    return "";
  }
  let h12 = hour24 % 12;
  if (h12 === 0) {
    h12 = 12;
  }
  const period = hour24 < 12 ? "am" : "pm";
  return `${h12}.00${period}`;
}

export function sortByOrderDateTime(o1, o2) {
  let result = 0;

  try {
    result = o1.fulfillment.date.localeCompare(o2.fulfillment.date);
    if (result === 0) {
      // if date is same, sort by time
      result = getStartTime(o1.fulfillment.time) - getStartTime(o2.fulfillment.time);
    }
  } catch (error) {
    console.log("invalid order date/time");
  }
  return result;
}

export function generateSizeSummary(variant, productType) {
  let arr = [];

  if (productType) {
    arr.push(productType);
  }

  if (variant) {
    if (variant.name) {
      arr.push(variant.name);
    } else {
      if (variant.shape) {
        arr.push(variant.shape);
      }
      if (variant.size) {
        arr.push(variant.size);
      }
      if (variant.servings) {
        arr.push(`${variant.servings} servings`);
      }
    }
  }

  return arr.join(" • ");
}

export function generateFlavorSummary(variant) {
  let summary = "";
  if (variant) {
    const flavor = normalizeFlavor(variant.flavor);

    if (flavor.name) {
      summary = `Flavor: ${flavor.name}`;
    }
    if (flavor.filling) {
      summary += ` • Filling: ${flavor.filling}`;
    } else if (variant.filling) {
      summary += ` • Filling: ${variant.filling.name}`;
    }
  }

  return summary;
}

export function getMaxAllowedRefund(order) {
  const totalPaid = currency(order.payment.paid);
  const totalRefunds = currency(
    (order.payment.refund || []).reduce((acc, obj) => {
      return +acc + +obj.amount;
    }, 0)
  );
  return totalPaid.subtract(totalRefunds).value;
}

export function getPolicyBasedRefund(order) {
  let cxlFee = 0;

  const advanceBookingDays = moment(order.fulfillment.date).diff(moment(), "days");
  console.log("advanceBookingDays", advanceBookingDays);

  if (advanceBookingDays >= 22) {
    cxlFee = currency(order.payment.total).multiply(0.25);
  } else if (advanceBookingDays >= 8) {
    cxlFee = currency(order.payment.total).multiply(0.5);
  } else {
    cxlFee = currency(order.payment.total)
      .subtract(order.payment.tip)
      .subtract(order.payment.delivery);
  }

  let refundAmount = currency(getMaxAllowedRefund(order)).subtract(cxlFee);

  return +refundAmount > 0 ? refundAmount.toString() : 0;
}

export function getSaasWebsiteUrl(domain) {
  if (domain.includes("localhost")) {
    return `http://localhost:3001`;
  } else {
    return `https://app.${domain}`;
  }
}

export function slugify(str) {
  str = String(str || "").replace(/^\s+|\s+$/g, ""); // trim leading/trailing white space
  str = str.toLowerCase(); // convert string to lowercase
  str = str
    .replace(/[^a-z0-9 -]/g, "") // remove any non-alphanumeric characters
    .replace(/\s+/g, "-") // replace spaces with hyphens
    .replace(/-+/g, "-"); // remove consecutive hyphens
  return str;
}

export function getItemUrl(item) {
  const category = encodeURIComponent(
    slugify(item.category?.length > 0 ? item.category[0] : item.type)
  );
  const slug = encodeURIComponent(slugify(`${item.title}`));
  return `/shop/${category}/${slug}-${item.id}`;
}

export function hasManagerAccess(role) {
  return role === "owner" || role === "manager" || role === "baker" || role === "rep";
}

export function hasOwnerAccess(role) {
  return role === "owner" || role === "baker";
}

export function orderMatchesStoreFilter(order, streetOrId, storeLocations = []) {
  if (!streetOrId) {
    return true;
  }
  const loc = (storeLocations || []).find(
    (l) => l.id === streetOrId || equalsIgnoreCase(l.address?.street, streetOrId)
  );
  if (order.fulfillment?.storeLocationId && loc?.id) {
    return order.fulfillment.storeLocationId === loc.id;
  }
  if (order.fulfillment?.location?.street) {
    return equalsIgnoreCase(order.fulfillment.location.street, streetOrId);
  }
  return Boolean(
    order.fulfillment?.address?.toLowerCase().startsWith(String(streetOrId).toLowerCase())
  );
}

export function getStoreFilterStreets(user) {
  const all = user?.config?.storeLocation || [];
  const ids = Array.isArray(user?.storeLocationIds) ? user.storeLocationIds.filter(Boolean) : [];
  if (!all.length || !ids.length) {
    return [];
  }
  const idSet = new Set(ids);
  return all
    .filter((l) => idSet.has(l.id))
    .map((l) => l.address?.street)
    .filter(Boolean);
}

export function getStoreLocationIdForFilter(user, streetOrId) {
  if (!streetOrId) {
    return "";
  }
  const loc = (user?.config?.storeLocation || []).find(
    (l) => l.id === streetOrId || equalsIgnoreCase(l.address?.street, streetOrId)
  );
  return loc?.id || "";
}

export function getVariantTotal(variant, basePrice) {
  let total = 0;
  if (variant) {
    for (const [key, value] of Object.entries(variant)) {
      if (value?.pricing) {
        if (value.pricing.amount) {
          total += +value.pricing.amount;
        } else if (value.pricing.factor) {
          total += currency(basePrice).multiply(value.pricing.factor).value;
        }
      } else if (typeof value === "object") {
        total += getVariantTotal(value, basePrice);
      }
    }
  }
  return +total;
}

export function getVariantOptionSurchargeForDisplay(option, basePrice) {
  let surcharge = "";
  if (+option.pricing?.amount > 0) {
    surcharge = currency(option.pricing?.amount).format();
  } else if (+basePrice > 0 && +option.pricing?.factor > 0) {
    surcharge = `${currency(basePrice).multiply(option.pricing?.factor).format()}`;
  } else if (+option.pricing?.factor > 0) {
    surcharge = `${(option.pricing.factor * 100).toFixed(2)}%`;
  }

  return surcharge;
}

export function getItemBasePrice(variant) {
  if (variant) {
    for (const [key, value] of Object.entries(variant)) {
      if (key === "size" && value?.pricing) {
        return +value?.pricing?.amount;
      }
    }
  }

  return 0;
}

export function getItemPrice(variant, basePrice) {
  if (!variant || typeof variant !== "object") {
    return 0;
  }
  let total = 0;
  for (const [key, value] of Object.entries(variant)) {
    if (value?.pricing) {
      if (value.pricing.amount) {
        total += +value.pricing.amount;
      } else if (value.pricing.factor) {
        total += currency(basePrice).multiply(value.pricing.factor).value;
      }
    } else if (value && typeof value === "object") {
      total += getItemPrice(value, basePrice);
    }
  }
  return +total;
}

export function getItemSurchargeTotal(variant, basePrice) {
  let total = 0;
  if (variant) {
    for (const [key, value] of Object.entries(variant)) {
      if (key === "size") {
        continue;
      }

      if (value?.pricing) {
        if (value.pricing.amount) {
          total += +value.pricing.amount;
        } else if (value.pricing.factor) {
          total += currency(basePrice).multiply(value.pricing.factor).value;
        }
      } else {
        total += getVariantTotal(value, basePrice);
      }
    }
  }
  return +total;
}

export function findOptionByName(options, name) {
  return (options || []).find((o) => o.name === name);
}

/**
 * True when pricing on the user's current select choices differs between two field snapshots
 * (same option names still available in both).
 */
export function selectionsPricingChanged(prevFields, nextFields, variant) {
  if (!prevFields || !nextFields || !variant) return false;
  let changed = false;
  for (const key of Object.keys(variant)) {
    if (key === "size") continue;
    const pf = prevFields[key];
    const nf = nextFields[key];
    if (!pf?.field || pf.field.type !== "select" || !nf?.field || nf.field.type !== "select") {
      continue;
    }
    const v = variant[key];
    if (!v) continue;
    const prevOpts = pf.field.options || [];
    const nextOpts = nf.field.options || [];
    const isMulti = +pf.field.max > 1;
    const parts = v.value?.parts;

    if (parts != null) {
      for (const k of Object.keys(parts)) {
        const part = parts[k];
        if (!part?.name) continue;
        const po = findOptionByName(prevOpts, part.name);
        const no = findOptionByName(nextOpts, part.name);
        if (po && no && JSON.stringify(po.pricing || null) !== JSON.stringify(no.pricing || null)) {
          changed = true;
          break;
        }
      }
    } else if (isMulti && Array.isArray(v.value)) {
      for (const sel of v.value) {
        const po = findOptionByName(prevOpts, sel.name);
        const no = findOptionByName(nextOpts, sel.name);
        if (po && no && JSON.stringify(po.pricing || null) !== JSON.stringify(no.pricing || null)) {
          changed = true;
          break;
        }
      }
    } else {
      const name = v.value?.name ?? v.name;
      if (!name) continue;
      const po = findOptionByName(prevOpts, name);
      const no = findOptionByName(nextOpts, name);
      if (po && no && JSON.stringify(po.pricing || null) !== JSON.stringify(no.pricing || null)) {
        changed = true;
      }
    }
  }
  return changed;
}

/**
 * Removes select values that are no longer present in `fields` (e.g. after rules hide options).
 * Returns `{ variant, adjusted }` where `adjusted` means an existing selection was removed or trimmed.
 * For flavor/filling with `value.parts`, any invalid part clears both fields (user re-selects via FlavorSelection).
 */
export function sanitizeVariantAgainstFields(variant, fields, basePrice) {
  if (!variant || !fields) return { variant, adjusted: false };
  let next = { ...variant };
  let adjusted = false;

  for (const [key, fieldConfig] of Object.entries(fields)) {
    if (key === "size") continue;
    if (fieldConfig.field?.type !== "select") continue;
    const v = next[key];
    if (!v) continue;
    const options = fieldConfig.field.options || [];
    const names = new Set(options.map((o) => o.name));
    const isMulti = +fieldConfig.field?.max > 1;
    const parts = v.value?.parts;

    if (parts != null) {
      const hasInvalidPart = Object.keys(parts).some(
        (k) => parts[k]?.name && !names.has(parts[k].name)
      );
      if (hasInvalidPart) {
        adjusted = true;
        if (key === "flavor" || key === "filling") {
          const { flavor: _f, filling: _fi, ...rest } = next;
          next = rest;
        } else {
          const { [key]: _removed, ...rest } = next;
          next = rest;
        }
      }
    } else if (isMulti && Array.isArray(v.value)) {
      const vals = v.value;
      const filtered = vals.filter((o) => o && names.has(o.name));
      if (filtered.length !== vals.length) {
        adjusted = true;
        if (filtered.length === 0) {
          const { [key]: _removed, ...rest } = next;
          next = rest;
        } else {
          next = {
            ...next,
            [key]: {
              ...v,
              name: filtered.map((o) => o.name).join(", "),
              pricing: { amount: getVariantTotal(filtered, basePrice) },
              value: filtered,
            },
          };
        }
      }
    } else {
      const name = v.value?.name ?? v.name;
      if (name && !names.has(name)) {
        adjusted = true;
        const { [key]: _removed, ...rest } = next;
        next = rest;
      }
    }
  }

  return { variant: adjusted ? next : variant, adjusted };
}

/**
 * Re-applies canonical option objects from `fields` onto existing selections so stored pricing
 * matches rule-driven option rows (e.g. fixed amounts from overrides).
 */
export function applyVariantPricingFromFields(variant, fields, basePrice) {
  if (!variant || !fields) return { variant, changed: false };
  let next = { ...variant };
  let changed = false;

  for (const [key, fieldConfig] of Object.entries(fields)) {
    if (fieldConfig.field?.type !== "select") continue;
    const v = next[key];
    if (!v) continue;
    const options = fieldConfig.field.options || [];
    const isMulti = +fieldConfig.field?.max > 1;
    const parts = v.value?.parts;

    if (parts != null) {
      const filteredParts = {};
      for (const k of Object.keys(parts).sort((a, b) => +a - +b)) {
        const optFromField = findOptionByName(options, parts[k]?.name);
        if (optFromField) {
          const { override, ...opt } = optFromField;
          filteredParts[k] = opt;
        }
      }
      if (!Object.keys(filteredParts).some((k) => filteredParts[k]?.name)) continue;
      const newEntry = {
        ...v,
        name: v.name,
        rank: v.rank || fieldConfig.rank || 1,
        pricing: { amount: getVariantTotal(filteredParts, basePrice) },
        value: { ...v.value, parts: filteredParts },
      };
      const before = next[key];
      if (
        JSON.stringify(before?.value?.parts) !== JSON.stringify(newEntry.value?.parts) ||
        before?.value?.selection !== newEntry.value?.selection ||
        before?.pricing?.amount !== newEntry.pricing?.amount
      ) {
        next = { ...next, [key]: newEntry };
        changed = true;
      }
    } else if (isMulti && Array.isArray(v.value)) {
      const picked = v.value.map((sel) => findOptionByName(options, sel.name)).filter(Boolean);
      if (picked.length === 0) continue;
      const newEntry = {
        name: picked.map((o) => o.name).join(", "),
        rank: v.rank || fieldConfig.rank || 1,
        pricing: { amount: getVariantTotal(picked, basePrice) },
        value: picked.map((o) => {
          const { override, ...opt } = o;
          return opt;
        }),
      };
      if (JSON.stringify(next[key]) !== JSON.stringify(newEntry)) {
        next = { ...next, [key]: newEntry };
        changed = true;
      }
    } else {
      const name = v.value?.name ?? v.name;
      if (!name) continue;
      const optFromField = findOptionByName(options, name);
      if (!optFromField) continue;
      const { override, ...opt } = optFromField;
      const newEntry = { ...opt, value: opt };
      if (v.rank != null) {
        newEntry.rank = v.rank;
      }
      if (JSON.stringify(next[key]) !== JSON.stringify(newEntry)) {
        next = { ...next, [key]: newEntry };
        changed = true;
      }
    }
  }
  return { variant: next, changed };
}

export function getItemMetadata(product) {
  let [minPrice, maxPrice, leadTime] = [0, 0, 0];
  let dietary = new Set();
  if (product.variants?.size && product.variants?.size?.field?.options) {
    for (const variant of Object.values(product.variants.size.field.options)) {
      if (variant.pricing) {
        const price = +variant.pricing.amount;
        if (minPrice === 0) {
          minPrice = price;
        } else if (price <= minPrice) {
          minPrice = price;
        } else if (price >= maxPrice) {
          maxPrice = price;
        }
      }

      const title = variant.name.toLowerCase();
      if (title.includes("vegan")) {
        dietary.add("VG");
      }

      if (title.includes("gluten") && title.includes("free")) {
        dietary.add("GF");
      }

      leadTime = product.rules?.date?.leadTime || 0;
    }
  }

  return { price: { min: minPrice, max: maxPrice }, dietary: Array.from(dietary), leadTime };
}

export function isTruncated(element) {
  if (
    element &&
    (element.offsetHeight < element.scrollHeight || element.offsetWidth < element.scrollWidth)
  ) {
    return true;
  } else {
    return false;
  }
}

export function normalizeFlavor(value) {
  if (Array.isArray(value)) {
    return value[0];
  } else if (typeof value === "object" && value.name) {
    // v2.0 format
    return value;
  } else if (typeof value === "object") {
    return { name: value?.base?.name, filling: value?.filling?.name };
  } else {
    return "";
  }
}

export function isAllowedByFilter(order, filter) {
  if (filter === "standard" && order.type !== "shop") {
    return false;
  } else if (filter === "custom" && order.type === "shop") {
    return false;
  } else {
    return true;
  }
}

export function sortByNumericPrefix(s1, s2) {
  let result = "";

  try {
    let num1 = s1.replace(/(^\d+)(.+$)/i, "$1");
    let num2 = s2.replace(/(^\d+)(.+$)/i, "$1");

    if (num1 && num2) {
      // compare as numbers
      result = num1 - num2;
      if (result === 0) {
        result = s1.localeCompare(s2);
      }
    } else {
      result = s1.localeCompare(s2);
    }

    return result;
  } catch (error) {
    console.log("invalid values");
  }
  return result;
}

export function calculatePaymentSummary(items, deliveryFee, hasDeposit, depositAmount) {
  const subtotal = currency(items.reduce((acc, o) => +acc + +o.total, 0));
  const delivery = currency(deliveryFee || 0);

  const total = subtotal.add(delivery);
  let due = total;

  if (hasDeposit && +depositAmount <= total.value) {
    due = currency(depositAmount);
  } else {
    // pay in full
  }

  return { subtotal: subtotal.value, delivery: delivery.value, total: total.value, due: due.value };
}

/**
 * Order/quote/cart-level master switch under settings.
 * When false, no sales tax is collected (e.g. wholesale).
 * When true, tax applies only to items with salesTax.enabled at product level.
 * Falls back to legacy settings.includeSalesTax for older quotes/orders.
 */
export function shouldCollectSalesTax(entity) {
  if (!entity) {
    return false;
  }
  if (entity.settings?.collectSalesTax !== undefined) {
    return `${entity.settings.collectSalesTax}` === "true";
  }
  // Brief mid-branch top-level writes, if any
  if (entity.collectSalesTax !== undefined) {
    return `${entity.collectSalesTax}` === "true";
  }
  return `${entity.settings?.includeSalesTax}` === "true";
}

export function calculateSalesTax(
  items,
  { subtotal, discount, delivery, platformFee = 0, salesTaxConfig, collectSalesTax }
) {
  if (`${collectSalesTax}` === "false") {
    return currency(0);
  }
  if (!salesTaxConfig || `${salesTaxConfig.enabled}` !== "true" || !(+salesTaxConfig.rate > 0)) {
    return currency(0);
  }
  const rate = +salesTaxConfig.rate;
  const sub = currency(subtotal);
  const discountedSubtotal = sub.subtract(discount || 0);

  let taxableSubtotal = currency(0);
  for (const item of items || []) {
    // Legacy lines missing salesTax: treat as taxable (in-flight carts / old quotes).
    const enabled =
      item?.salesTax?.enabled === undefined ? true : `${item.salesTax.enabled}` === "true";
    if (enabled) {
      taxableSubtotal = taxableSubtotal.add(+item.total || +item.price || 0);
    }
  }

  const ratio = subtotal > 0 ? taxableSubtotal.value / subtotal : 0;

  const taxableBase = discountedSubtotal
    .add(currency(delivery || 0))
    .add(currency(platformFee || 0))
    .multiply(ratio);

  return taxableBase.multiply(rate);
}

export function recalculatePayment(
  items,
  fulfillment,
  discount,
  hasDeposit,
  depositAmount,
  salesTaxConfig
) {
  const subtotal = currency(items.reduce((acc, o) => +acc + (+o.total || +o.price), 0));
  discount = currency(discount || 0);
  const discountedSubtotal = subtotal.subtract(discount);
  const delivery = currency(
    (fulfillment?.type === "delivery" && fulfillment?.option?.delivery?.total) || 0
  );
  const tipAmount = discountedSubtotal.add(delivery).multiply(0).divide(100);

  const salesTax = calculateSalesTax(items, {
    subtotal: subtotal.value,
    discount: discount.value,
    delivery: delivery.value,
    platformFee: 0,
    salesTaxConfig,
  });

  const total = discountedSubtotal.add(delivery).add(tipAmount).add(salesTax);
  let due = total;

  if (hasDeposit && depositAmount && +depositAmount >= 0 && +depositAmount <= total.value) {
    due = currency(depositAmount);
  } else {
    // pay in full
  }

  return {
    subtotal: subtotal.value,
    discount: discount.value,
    delivery: delivery.value,
    tip: tipAmount.value,
    salesTax: salesTax.value,
    total: total.value,
    due: due.value,
  };
}

export function isEmailValid(email) {
  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);
}

export function isPhoneValid(phone) {
  return phone && phone.length >= 12;
}

export function getFormattedAddress(location) {
  return `${location.street}, ${location.city}, ${location.state} ${location.zip}`;
}

export function getFormattedAddressAsArray(location, includeUnit) {
  let arr = [];

  if (location) {
    let line1 = location.street;
    if (includeUnit && location.unit) {
      line1 += `, ${location.unit}`;
    }
    arr.push(line1);
    arr.push(`${location.city}, ${location.state} ${location.zip}`);
  }
  return arr;
}

export function equalsIgnoreCase(str1, str2) {
  if (str1 && str2) {
    return str1.trim().toLowerCase() === str2.trim().toLowerCase();
  }
  return false;
}

export function arrayContainsIgnoreCase(arr, str) {
  if (arr && str) {
    return arr.filter((o) => o.trim().toLowerCase() == str.trim().toLowerCase()).length > 0;
  }
  return false;
}

export function isAdmin(uid) {
  return uid === "j2tO1dfIh9ZnUnb40p4lK60gXaI2" || uid === "hNGQFaTpqOYdtnWpoClwsHe49fq2";
}

export function anyWordStartsWith(string, prefix) {
  string = string.toLowerCase();
  prefix = prefix.toLowerCase();

  if (string.startsWith(prefix)) {
    return true;
  }

  let tokens = string.toLowerCase().split(" ");
  for (const token of tokens) {
    if (token.trim().startsWith(prefix)) {
      return true;
    }
  }

  return false;
}

export function isTouchDevice() {
  return matchMedia("(pointer:fine)").matches;
}

export function getAddOnSummary(addOnVariant) {
  if (Array.isArray(addOnVariant)) {
    // old format: Delete
    return addOnVariant.map((o) => o.name).join(", ");
  } else {
    // New format
    return addOnVariant?.name;
  }
}

export function convertOldFlavorOptions(name, options, rank) {
  let obj = {
    name,
    description: "",
    rank,
    required: true,
    field: {
      type: "select",
      min: 1,
      max: 1,
    },
  };
  obj.field.options = options.map((o) => {
    return {
      name: o.name,
      description: o.desc,
      pricing: {
        type: "fixed",
        amount: o.fee,
      },
    };
  });

  return obj;
}

export function isNumeric(str) {
  return (
    !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !isNaN(parseFloat(str))
  ); // ...and ensure strings of whitespace fail
}

export function getCategoryRank(cat, arr) {
  cat = cat.toLowerCase();
  if (cat.includes("featured")) return 10000;
  if (cat.includes("easter") || cat.includes("ramadan") || cat.includes("passover")) return 9000;

  const rank = arr.reduce((acc, o) => +acc + +o.rank, 0);
  const itemsWithEndDate = arr.filter((o) => o.rules?.date?.max);

  if (itemsWithEndDate.length > 0 && itemsWithEndDate.length / arr.length > 0.4) {
    // if half of items in category have end date, sort by end date
    return 8000;
  } else if (rank > arr.length) {
    // sort by bestsellers
    return rank;
  } else {
    // sort by number of items in category
    return arr.length; // all other strings
  }
}

export function sortCategories([k1, v1], [k2, v2]) {
  const rankA = getCategoryRank(k1, v1);
  const rankB = getCategoryRank(k2, v2);

  if (rankA === rankB) {
    return k1.localeCompare(k2);
  } else {
    return rankB - rankA;
  }
}

export function validateFulfillment(fulfillment) {
  let arr = [];
  if (!fulfillment.type) {
    arr.push("Fulfillment Type is missing");
  }
  if (!fulfillment.location) {
    if (fulfillment.type === "pickup") {
      arr.push("Pickup Location is missing");
    } else if (fulfillment.type === "delivery") {
      arr.push("Delivery Address is missing");
    }
  }
  if (!fulfillment.date) {
    arr.push("Date is missing");
  }
  if (!fulfillment.time) {
    arr.push("Time is missing");
  }
  if (fulfillment.type === "delivery") {
    if (!fulfillment.option?.delivery?.amount) {
      arr.push("Delivery Fee is missing");
    }
  }

  return arr;
}

export function validateCustomer(customer) {
  let arr = [];
  if (!customer.firstName) {
    arr.push("First Name is missing");
  }
  if (!customer.phone) {
    arr.push("Phone is missing");
  } else if (!isPhoneValid(customer.phone)) {
    arr.push("Phone number is invalid");
  }
  if (customer.email && !isEmailValid(customer.email)) {
    arr.push("Email is incorrect");
  }
  return arr;
}

export function isCustomerEmpty(c) {
  if (!c) return true;
  return (
    !c.firstName?.trim() &&
    !c.lastName?.trim() &&
    !c.email?.trim() &&
    (c.phone || "").replace(/\D/g, "").length <= 1
  );
}

export function getDeliveryProviders(deliveryConfig) {
  let arr = [];

  if (deliveryConfig) {
    if (deliveryConfig?.providers) {
      for (const [key, value] of Object.entries(deliveryConfig.providers)) {
        arr.push({ key, value });
      }
    } else if (deliveryConfig?.["third-party"] === "metrobi") {
      arr.push({ key: "metrobi", value: { name: "Metrobi" } });
    }
  }

  arr.push({ key: "in-house", value: { name: "In-house" } });

  return arr.sort((o1, o2) => o1.key.localeCompare(o2.key));
}

export function getDeliveryProviderDisplayName(type, deliveryConfig) {
  if (!type || type === "no") {
    return "";
  }
  if (type === "in-house") {
    return "In-house";
  }
  const provider = deliveryConfig?.providers?.[type];
  if (provider?.name) {
    return provider.name;
  }
  return capitalize(type);
}

export function buildInvoiceQuoteFulfillment(fulfillment) {
  const f = {
    date: fulfillment.date,
    option: { pickup: {}, delivery: { type: "no" } },
  };
  if (fulfillment.time) {
    f.time = fulfillment.time;
  }

  if (fulfillment.type !== "delivery") {
    return f;
  }

  const providerName = fulfillment.provider?.name || "in-house";
  f.option.delivery = { ...(fulfillment.option?.delivery || {}) };
  f.option.delivery.type = providerName;

  if (fulfillment.location) {
    f.option.delivery.location = fulfillment.location;
  }

  return f;
}

export function isSalesTaxEnabled(userConfig) {
  return `${userConfig?.salesTax?.enabled}` === "true";
}

/**
 * Default toggle state for new products and custom/line items.
 * Uses config.salesTax.default when set; otherwise falls back to enabled.
 * Not used for order/quote collectSalesTax or tax calculation.
 */
export function isSalesTaxDefaultEnabled(userConfig) {
  if (userConfig?.salesTax?.default !== undefined) {
    return `${userConfig.salesTax.default}` === "true";
  }
  return isSalesTaxEnabled(userConfig);
}

/**
 * Product-level sales tax: explicit product.salesTax.enabled if set,
 * otherwise the bakery default (same as the product editor toggle).
 */
export function getSalesTaxEnabledFromProduct(productSalesTax, userConfig) {
  if (productSalesTax?.enabled === undefined) {
    return isSalesTaxDefaultEnabled(userConfig);
  }
  return `${productSalesTax.enabled}` === "true";
}

export function getOrdinalSuffix(number) {
  if (typeof number !== "number" || !Number.isInteger(number)) {
    // Or throw an error: throw new Error("Input must be an integer.");
    return String(number); // Handle non-integer or non-numeric input gracefully
  }

  const lastTwoDigits = number % 100;
  const lastDigit = number % 10;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return number + "th";
  }

  switch (lastDigit) {
    case 1:
      return number + "st";
    case 2:
      return number + "nd";
    case 3:
      return number + "rd";
    default:
      return number + "th";
  }
}

// Alphanumeric-only key for catalog lookup (ignores spaces, hyphens, punctuation).
export function normalizeCatalogKey(str) {
  if (!str) {
    return "";
  }
  return String(str)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/** Resolve a catalog entry by product type (display name or catalog key; ignores spaces/punctuation). */
export function getCatalogEntry(userConfig, productType) {
  const catalog = userConfig?.catalog;
  if (!catalog || !productType?.trim()) {
    return null;
  }

  const normalizedType = normalizeCatalogKey(productType);

  const entryKey = Object.keys(catalog).find((key) => normalizeCatalogKey(key) === normalizedType);
  if (entryKey) {
    return catalog[entryKey];
  }

  return (
    Object.values(catalog).find((entry) => normalizeCatalogKey(entry.name) === normalizedType) ||
    null
  );
}

// Returns the variants array for a given productType from user config
export function getProductTypeVariants(userConfig, productType) {
  const variantsObj = getCatalogEntry(userConfig, productType)?.variants || {
    size: {
      name: "Size",
      description: "",
      rank: 8,
      required: true,
      field: {
        type: "select",
        min: 1,
        max: 1,
        options: [
          { name: "Mini" },
          { name: "Small" },
          { name: "Standard" },
          { name: "Large" },
          { name: "Jumbo" },
        ],
      },
    },
  };

  // Return a new object with required set to false for each field
  return Object.fromEntries(
    Object.entries(variantsObj).map(([key, field]) => [key, { ...field, required: false }])
  );
}

export function cacheCatalogFieldOptions(items) {
  const cache = { category: new Set() };
  items.forEach((item) => {
    if (item.category && Array.isArray(item.category)) {
      item.category.forEach((o) => cache.category.add(o));
    }

    if (!item?.variants) return;

    // check variants
    Object.entries(item.variants).forEach(([variantKey, variantObj]) => {
      if (variantObj.field.type === "select") {
        const options = variantObj?.field?.options;
        if (Array.isArray(options)) {
          let variantOptions = cache[variantKey] || new Set();
          variantObj?.field?.options.forEach((o) => variantOptions.add(o.name));
          cache[variantKey] = variantOptions;
        }
      }
    });
  });
  // Convert all Set values in cache to arrays
  Object.keys(cache).forEach((key) => {
    if (cache[key] instanceof Set) {
      cache[key] = Array.from(cache[key]).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" })
      );
    }
  });
  setDataInLocalStorage("catalog.options", cache);
}

export function getCatalogOptionsByFieldName(name) {
  let arr = [];
  const cache = getDataFromLocalStorage("catalog.options");
  if (cache && cache[name]) {
    arr = cache[name];
  }
  return arr;
}

export function getCategoryOptions() {
  const cache = getCatalogOptionsByFieldName("category");
  const allCategories = Array.from(new Set([...cache, ...CATEGORIES]));
  return allCategories.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

// Keep in sync with getDefaultProgressSteps in firebase-functions and admin.
// Each item's progress stores its own steps, so changing these defaults never moves items already in production.
// `progressSteps` is user.config.kds.progressSteps: item type -> ordered steps, matched case-insensitively.
export function getDefaultProgressSteps(item, progressSteps) {
  const key =
    item.type && Object.keys(progressSteps || {}).find((k) => equalsIgnoreCase(k, item.type));
  if (key && Array.isArray(progressSteps[key]) && progressSteps[key].length > 0) {
    return progressSteps[key];
  }
  return !item.productId && equalsIgnoreCase(item.type, "cake")
    ? ORDER_PROGRESS_STEPS
    : ITEM_PROGRESS_STEPS;
}

// Maps a pre-item-level order stage onto an item's own stages: done stays done, started stays started.
function convertLegacyProgressStep(legacyProgress, steps) {
  const legacyStep = +legacyProgress?.step || 0;
  const legacyLength = legacyProgress?.steps?.length || ORDER_PROGRESS_STEPS.length;
  if (legacyStep >= legacyLength) {
    return steps.length;
  }
  return Math.min(legacyStep, steps.length - 1);
}

export function getItemProgress(order, item, progressSteps) {
  const steps = getDefaultProgressSteps(item, progressSteps);
  const saved = item.progress;
  if (saved) {
    return {
      step: readStoredProgressStep(saved.step),
      steps: normalizeProductionProgressSteps(saved.steps || steps),
    };
  } else if (order.progress && !order.items?.some((o) => o.progress)) {
    const legacyStep = convertLegacyProgressStep(order.progress, steps);
    // Order-level step 0 meant not started, which is a missing item step.
    return { step: legacyStep > 0 ? legacyStep : null, steps };
  }
  return { step: null, steps };
}

// Progress saves return stored lines, which lack the display data (variant `field`) added by GET /my/order/:orderId.
export function mergeItemProgress(prevItems, items) {
  if (prevItems.length !== items.length) {
    return items;
  }
  return prevItems.map((item, idx) => ({ ...item, progress: items[idx].progress }));
}

export function getProgressLane(progress) {
  const steps = normalizeProductionProgressSteps(progress?.steps);
  if (!steps.length || !hasStoredProgressStep(progress?.step)) {
    return "notStarted";
  }
  const completedCount = storedStepToDisplayActiveStep(progress.step, steps.length);
  if (completedCount >= steps.length) {
    return "completed";
  }
  return "inProgress";
}

export function getItemProgressStageLabel(progress) {
  const steps = normalizeProductionProgressSteps(progress?.steps);
  if (!steps.length || !hasStoredProgressStep(progress?.step)) {
    return PROGRESS_LANES.notStarted.label;
  }
  const completedCount = storedStepToDisplayActiveStep(progress.step, steps.length);
  if (completedCount >= steps.length) {
    return capitalize(steps[steps.length - 1]);
  }
  return capitalize(steps[completedCount]);
}

/** Number of production stages completed (for progress ring). */
export function productionStagesCompleted(storedStep, productionStepCount) {
  const count = +productionStepCount || 0;
  if (!count) {
    return 0;
  }
  return storedStepToDisplayActiveStep(storedStep, count);
}

/** Stored progress steps never include the display-only leading "Not started" step. */
export function normalizeProductionProgressSteps(steps) {
  const list = steps || [];
  if (list.length && equalsIgnoreCase(list[0], PROGRESS_LANES.notStarted.label)) {
    return list.slice(1);
  }
  return list;
}

/** Production stage labels for the stepper (no separate "Not started" row). */
export function getProgressStepsForDisplay(steps) {
  return normalizeProductionProgressSteps(steps);
}

/**
 * A missing step means not started. 0 means the first stage is in progress
 * (no stages completed). k means k stages are completed and stage k is in progress
 * while k is below the stage count. k >= count means every stage is complete.
 */
export function hasStoredProgressStep(storedStep) {
  if (storedStep === null || storedStep === undefined || storedStep === "") {
    return false;
  }
  return Number.isFinite(+storedStep);
}

export function readStoredProgressStep(storedStep) {
  if (!hasStoredProgressStep(storedStep)) {
    return null;
  }
  return +storedStep;
}

/** Completed production-stage count. Missing and 0 both mean none completed. */
export function storedStepToDisplayActiveStep(storedStep, productionStepCount) {
  const count = +productionStepCount || 0;
  const step = readStoredProgressStep(storedStep);
  if (step === null) {
    return 0;
  }
  return Math.min(Math.max(0, step), count);
}

export function displayActiveStepToStoredStep(displayFrontier) {
  return Math.max(0, +displayFrontier);
}

function getStepAfterDisplayClick(clickedStep, currentDisplayStep) {
  return clickedStep < currentDisplayStep ? +clickedStep : +clickedStep + 1;
}

export function getStoredStepAfterDisplayStepClick(clickedDisplayStep, storedStep, steps) {
  const productionSteps = normalizeProductionProgressSteps(steps);
  const displayActive = storedStepToDisplayActiveStep(storedStep, productionSteps.length);
  const newDisplayActive = getStepAfterDisplayClick(+clickedDisplayStep, displayActive);
  return displayActiveStepToStoredStep(newDisplayActive);
}

// Items can have different numbers of stages, so the order fill averages each item's fraction done.
export function deriveOrderProgress(order, progressSteps) {
  const items = order.items || [];
  const progressList =
    items.length > 0
      ? items.map((item) => getItemProgress(order, item, progressSteps))
      : [getItemProgress(order, {}, progressSteps)];

  const lanes = progressList.map(getProgressLane);
  let lane = "inProgress";
  if (lanes.every((o) => o === "completed")) {
    lane = "completed";
  } else if (lanes.every((o) => o === "notStarted")) {
    lane = "notStarted";
  }

  const totalFraction = progressList.reduce((acc, p) => {
    const steps = normalizeProductionProgressSteps(p.steps);
    const done = productionStagesCompleted(p.step, steps.length);
    return acc + (steps.length ? done / steps.length : 0);
  }, 0);
  return { pct: (totalFraction * 100) / progressList.length, lane };
}

export function isKDSMode(user) {
  if (getDataFromLocalStorage("kds") && getDataFromLocalStorage("kds-user")) {
    return true;
  } else if (user && user.role === "kitchen") {
    return true;
  } else if (user && user.role === "kitchen leader") {
    return true;
  }
  return false;
}

export function isPOSMode(user) {
  if (getDataFromLocalStorage("pos") && getDataFromLocalStorage("pos-user")) {
    return true;
  } else if (user && user.role === "clerk") {
    return true;
  }
  return false;
}

export function isSharedPosStaffLoggedIn() {
  return Boolean(getDataFromLocalStorage("pos") && getDataFromLocalStorage("pos-user"));
}

export const POS_IDLE_WARNING_SECONDS = 10;

export function getPosInactivityTimeoutSeconds(user) {
  const seconds = Number(user?.config?.inactivityTimeout);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return 0;
  }
  return seconds;
}

/** Logs off the current POS staff PIN session. Device stays in POS mode. */
export function logOffPosStaff(navigate) {
  if (!isSharedPosStaffLoggedIn()) {
    return false;
  }
  localStorage.removeItem("pos-user");
  navigate("/access-code");
  return true;
}

export function getSharedDeviceUid() {
  if (getDataFromLocalStorage("pos")) {
    return getDataFromLocalStorage("pos").uid;
  } else if (getDataFromLocalStorage("kds")) {
    return getDataFromLocalStorage("kds").uid;
  } else {
    return "";
  }
}

/** Upcoming dates (YYYY-MM-DD) this item is unavailable, from rules.date.specialHours. */
export function getItemBlackoutDates(product) {
  const specialHours = product.rules?.date?.specialHours || {};
  const today = moment().format("YYYY-MM-DD");

  return Object.keys(specialHours)
    .filter((d) => d >= today)
    .sort();
}

/** Short summary of upcoming blackout dates, e.g. "Unavailable Dec 24, Dec 25, Dec 26 +1". */
export function getItemBlackoutSummary(product, limit = 3) {
  const dates = getItemBlackoutDates(product);
  if (dates.length === 0) {
    return "";
  }

  const shown = dates.slice(0, limit).map((d) => moment(d, "YYYY-MM-DD").format("MMM D"));
  const rest = dates.length - shown.length;

  return `Unavailable ${shown.join(", ")}${rest > 0 ? ` +${rest}` : ""}`;
}

export function getItemDateRules(product) {
  let arr = [];
  let leadTime = product.rules?.date?.leadTime || 0;
  if (leadTime > 0) {
    arr.push(`${leadTime} hours lead time`);
  }
  if (product.rules?.date) {
    let dateRange = "";
    if (product.rules.date?.min && product.rules?.date?.max) {
      dateRange = `${moment(product.rules.date.min).format("MMM D")} - ${moment(product.rules?.date?.max).format("MMM D")}`;
    } else if (product.rules.date?.min) {
      dateRange = `${moment(product.rules?.date?.min).format("MMM D")} onwards`;
    } else if (product.rules.date?.max) {
      dateRange = `Until ${moment(product.rules?.date?.max).format("MMM D")}`;
    }
    if (dateRange) {
      arr.push(dateRange);
    }

    if (product.rules.date.daysToExclude) {
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].filter(
        (item) =>
          !product.rules.date.daysToExclude.join(",").toLowerCase().includes(item.toLowerCase())
      );
      if (days.length < 7) {
        let daysOfWeek = `${days.join(", ")} only`;
        arr.push(daysOfWeek);
      }
    }
  }
  return arr;
}

export function getVariantOptionsByItem(catalog, item) {
  let variants = "";
  const configByItemType = getCatalogEntry({ catalog }, item.type);
  if (configByItemType?.variants) {
    variants = configByItemType.variants;
    for (const [key, value] of Object.entries(configByItemType.variants)) {
      if (value.field.type === "select") {
        value.field.options = getOptionListByOverride(value.field.options, {
          size: item.variant.size.name,
        });
      }
    }
  }

  return variants;
}

export function getOptionListByOverride(options, overrides) {
  return (options || []).reduce((result, option) => {
    const resolved = resolveVariantOptionOverrides(option, overrides);
    if (resolved.allowed) result.push(resolved.option);
    return result;
  }, []);
}

/** Catalog rules array for a product type, or [] if none. */
export function getCatalogRules(userConfig, productType) {
  const rules = getCatalogEntry(userConfig, productType)?.rules;
  if (!Array.isArray(rules) || rules.length === 0) {
    return [];
  }
  return rules;
}

/** True when the catalog entry for `itemType` has at least one pricing/override rule. */
export function hasCatalogRules(userConfig, itemType) {
  return getCatalogRules(userConfig, itemType).length > 0;
}

/**
 * Facts for one catalog rules run: size plus every variant that already has a selection.
 */
export function buildCatalogRuleFacts(item) {
  const facts = {};
  const v = item?.variant || {};
  if (v.size?.name) {
    facts.size = v.size.name;
  }
  for (const [k, val] of Object.entries(v)) {
    if (k === "size") {
      continue;
    }
    if (val && typeof val.name === "string") {
      facts[k] = val.name;
    }
  }
  return facts;
}

/**
 * Like getVariantsBySize but when catalog[itemType].rules is non-empty, evaluates surcharges
 * with json-rules-engine ({ allowUndefinedFacts: true }). One engine.run per line item snapshot;
 * for each option, params from every "override" event are merged in order (later wins per key).
 * If the merged rule row has no `amount`, catalog `option.pricing.amount` is used as fixed pricing.
 */
export async function getVariantsBySizeWithRules(variants, item, catalogEntry) {
  const tTotal = performance.now();

  const rules = catalogEntry?.rules;
  if (!Array.isArray(rules) || rules.length === 0) {
    const result = getVariantsBySize(variants, item);
    console.log(
      `[perf]   legacy path (no rules), total: ${(performance.now() - tTotal).toFixed(2)}ms`
    );
    return result;
  }

  const tClone = performance.now();
  const tmp = JSON.parse(JSON.stringify(variants));
  console.log(`[perf]   deep clone: ${(performance.now() - tClone).toFixed(2)}ms`);

  if (!item.variant?.size) {
    console.log(`[perf]   no size selected, total: ${(performance.now() - tTotal).toFixed(2)}ms`);
    return tmp;
  }

  applyPhotoPricingFromSize(tmp, item.variant);

  const tEngine = performance.now();
  const engine = new Engine([], { allowUndefinedFacts: true });

  for (const r of rules) {
    engine.addRule(r);
  }
  console.log(
    `[perf]   json-rules-engine setup: ${(performance.now() - tEngine).toFixed(2)}ms (${rules.length} rules)`
  );

  const facts = buildCatalogRuleFacts(item);
  console.log("[rules] facts", facts);
  const tRun = performance.now();
  const { events } = await engine.run(facts);
  const totalEngineRunTime = performance.now() - tRun;
  const totalEngineRunCalls = 1;
  console.log(
    `[perf]   json-rules-engine.run: ${(performance.now() - tRun).toFixed(2)}ms (1 call)`
  );

  const overrideEvents = events.filter((e) => e.type === "override");
  console.log("[rules] override events", overrideEvents.length, overrideEvents);
  if (overrideEvents.length === 0) {
    console.log(
      `[perf]   json-rules-engine.run called ${totalEngineRunCalls}x, total time: ${totalEngineRunTime.toFixed(2)}ms`
    );
    console.log(
      `[perf]   legacy path (no override event), total: ${(performance.now() - tTotal).toFixed(2)}ms`
    );
    return getVariantsBySize(variants, item);
  }

  for (const [key, value] of Object.entries(tmp)) {
    if (key === "size") {
      continue;
    }
    if (value.field.type !== "select" || !item.variant.size) {
      continue;
    }

    const rawOptions = JSON.parse(JSON.stringify(value.field.options || []));
    const nextOptions = [];
    const tField = performance.now();
    for (const option of rawOptions) {
      const { override, ...optClone } = option;

      let row = {};
      for (const e of overrideEvents) {
        const p = e.params && typeof e.params === "object" ? e.params : {};
        const partial = p[key]?.[optClone.name];
        if (partial && typeof partial === "object") {
          row = { ...row, ...partial };
        }
      }

      if (row?.allow === false || String(row?.allow) === "false") {
        continue;
      }

      if (option.pricing && +option?.pricing?.amount) {
        optClone.pricing = { type: "fixed", amount: +option?.pricing?.amount };
      }

      if (row != null && +row.amount) {
        optClone.pricing = { type: "fixed", amount: +row.amount };
      }

      nextOptions.push(optClone);
    }
    console.log(
      `[perf]   field "${key}": ${rawOptions.length} options → ${nextOptions.length} kept, ${(performance.now() - tField).toFixed(2)}ms`
    );
    tmp[key].field.options = nextOptions;
    if (tmp[key].field.options.length === 0) {
      delete tmp[key];
    }
  }
  console.log(
    `[perf]   json-rules-engine.run called ${totalEngineRunCalls}x, total time: ${totalEngineRunTime.toFixed(2)}ms`
  );
  console.log(
    `[perf]   TOTAL getVariantsBySizeWithRules: ${(performance.now() - tTotal).toFixed(2)}ms`
  );
  return tmp;
}

export function extractSizeRank(str) {
  // Extract the first number preceding the words "inch", '"' or "sheet"
  if (!str) return NaN;

  str = str.toLowerCase();
  if (str.includes("sheet")) {
    if (str.includes("quarter") || str.includes("1/4")) {
      return 100;
    } else if (str.includes("half") || str.includes("1/2")) {
      return 101;
    } else if (str.includes("full")) {
      return 102;
    }
  }

  // Improved regex: look for a number (integer or decimal) followed by optional space, optional dash, and then ("inch" or '"' or "sheet")
  // Handles cases like "14-inch", "14 inch", '14"'
  // Also matches numbers before "tier" as a fallback if no inch found
  let match = str.match(/(\d+(\.\d+)?)(?=\s*-?\s*(inch|\"|sheet))/i);

  if (!match) {
    // fallback: look for number before "tier"
    match = str.match(/(\d+(\.\d+)?)(?=\s*-?\s*tier)/i);
  }
  return match ? parseFloat(match[1]) : NaN;
}

/** Geometric form after inch size, e.g. "8-inch Round (Single Layer)" → "round" */
export function extractGeometricShape(str) {
  if (!str) return "";
  const match = str.match(/(?:\d+(?:\.\d+)?)\s*-?\s*(?:inch|")\s+([a-z]+)/i);
  return match ? match[1].toLowerCase() : "";
}

function extractLayerRank(option, label) {
  const layer = Number(option?.layer);
  if (option?.layer != null && option.layer !== "" && !isNaN(layer)) {
    return layer;
  }
  const str = (label || "").toLowerCase();
  if (str.includes("single")) return 1;
  if (str.includes("double")) return 2;
  if (str.includes("triple")) return 3;
  if (str.includes("quadruple") || /\bquad\b/.test(str)) return 4;
  const match = str.match(/(\d+)\s*layers?/);
  return match ? parseFloat(match[1]) : NaN;
}

export function sortVariantOptions(fieldKey, o1, o2) {
  if (fieldKey === "size") {
    const label1 = o1.size || o1.name;
    const label2 = o2.size || o2.name;
    const n1 = extractSizeRank(label1);
    const n2 = extractSizeRank(label2);

    if (!isNaN(n1) && !isNaN(n2)) {
      if (n1 !== n2) {
        return n1 - n2;
      }

      // Same inch: group by geometric shape (Round, Square, …)
      const shape1 = extractGeometricShape(label1);
      const shape2 = extractGeometricShape(label2);
      if (shape1 && shape2 && shape1 !== shape2) {
        return shape1.localeCompare(shape2);
      }

      // Then by layer count (single → double → triple)
      const layer1 = extractLayerRank(o1, label1);
      const layer2 = extractLayerRank(o2, label2);
      if (!isNaN(layer1) && !isNaN(layer2) && layer1 !== layer2) {
        return layer1 - layer2;
      }

      return (o1.name || label1 || "").localeCompare(o2.name || label2 || "") || 0;
    } else if (o1.pricing?.amount && o2.pricing?.amount) {
      return o1.pricing.amount - o2.pricing.amount;
    } else {
      return o1?.name?.localeCompare(o2?.name) || 0;
    }
  } else {
    return o1?.name?.localeCompare(o2?.name) || 0;
  }
}

/**
 * Photo field pricing matches select options / saas-app: size-override amount if present,
 * otherwise the field's flat-rate pricing.
 */
function getPhotoPricingForSize(field, sizeName) {
  const szOvr = sizeName && field?.override?.size?.[sizeName];
  if (szOvr?.amount) {
    return { amount: szOvr.amount, type: "surcharge" };
  }
  return field?.pricing;
}

/** Copy size-based photo override onto the field so the editor can show / persist surcharge. */
function applyPhotoPricingFromSize(fields, variant) {
  if (!fields || !variant?.size) return;
  for (const [key, value] of Object.entries(fields)) {
    if (key === "size") continue;
    if (value?.field?.type === "photo") {
      const pricing = getPhotoPricingForSize(value, variant.size.name);
      if (pricing) {
        value.pricing = pricing;
      } else {
        delete value.pricing;
      }
    }
  }
}

/** Build or clear the selected photo variant. Empty photo list removes the field (and its surcharge). */
export function buildPhotoVariantValue(fieldConfig, photos) {
  if (!Array.isArray(photos) || photos.length === 0) {
    return null;
  }
  const entry = {
    name: `${photos.length} ${photos.length === 1 ? "photo" : "photos"}`,
    rank: fieldConfig?.rank || 1,
    value: photos,
  };
  if (fieldConfig?.pricing) {
    entry.pricing = fieldConfig.pricing;
  }
  return entry;
}

/** Image urls uploaded into photo-type variant fields. */
export function getPhotoUrlsFromVariant(variant, fields) {
  const urls = [];
  if (!variant) return urls;
  for (const [key, value] of Object.entries(variant)) {
    if (!Array.isArray(value?.value)) continue;
    const isPhotoField = fields
      ? fields[key]?.field?.type === "photo"
      : value.value.every((v) => typeof v === "string");
    if (isPhotoField) {
      urls.push(...value.value.filter((v) => typeof v === "string"));
    }
  }
  return urls;
}

/**
 * Keep `item.images` in sync with photo variants so uploads show on Order Print / kitchen ticket.
 * Variant photos lead the list (Order Print shows images[0]); images the baker added stay behind them.
 */
export function syncItemImagesWithPhotoVariant(images, prevVariant, nextVariant, fields) {
  const prevPhotos = getPhotoUrlsFromVariant(prevVariant, fields);
  const nextPhotos = getPhotoUrlsFromVariant(nextVariant, fields);
  if (prevPhotos.length === 0 && nextPhotos.length === 0) {
    return images;
  }
  const rest = (images || [])
    .filter(Boolean)
    .filter((url) => !prevPhotos.includes(url) && !nextPhotos.includes(url));
  return [...nextPhotos, ...rest];
}

/** Photo field that absorbs images added outside the Photo accordion: the one in use, else the first. */
function getPrimaryPhotoFieldKey(variant, fields) {
  const photoKeys = Object.keys(fields || {}).filter((k) => fields[k]?.field?.type === "photo");
  if (photoKeys.length === 0) {
    return "";
  }
  const inUse = photoKeys.find(
    (k) => getPhotoUrlsFromVariant({ [k]: variant?.[k] }, fields).length
  );
  return inUse || photoKeys[0];
}

/**
 * Mirror item image gallery edits back onto the photo variant, so tiles & surcharge follow along.
 * Images in `catalogImages` (the product's own photos) never count as customer photos, and the
 * field's `max` caps how many are absorbed — extras stay as plain item images.
 */
export function syncPhotoVariantWithItemImages(
  images,
  prevImages,
  variant,
  fields,
  catalogImages = []
) {
  const key = getPrimaryPhotoFieldKey(variant, fields);
  if (!key) {
    return variant;
  }

  const fieldConfig = fields[key];
  const max = +fieldConfig?.field?.max || 1;
  const nextImages = (images || []).filter(Boolean);
  const prevPhotos = Array.isArray(variant?.[key]?.value) ? variant[key].value.filter(Boolean) : [];

  const kept = prevPhotos.filter((url) => nextImages.includes(url));
  const added = nextImages.filter(
    (url) =>
      !(prevImages || []).includes(url) && !kept.includes(url) && !catalogImages.includes(url)
  );
  const nextPhotos = [...kept, ...added].slice(0, max);

  if (
    nextPhotos.length === prevPhotos.length &&
    nextPhotos.every((url, idx) => url === prevPhotos[idx])
  ) {
    return variant;
  }

  const next = { ...(variant || {}) };
  const photoVal = buildPhotoVariantValue(fieldConfig, nextPhotos);
  if (photoVal) {
    next[key] = photoVal;
  } else {
    delete next[key];
  }
  return next;
}

/**
 * Keep selected photo variants in sync with field pricing (size override / flat surcharge).
 * Drops the photo key when no images remain so the surcharge is removed.
 */
export function applyPhotoVariantPricing(variant, fields) {
  if (!variant || !fields) return variant || {};
  const next = { ...variant };
  let changed = false;
  for (const [key, fieldConfig] of Object.entries(fields)) {
    if (fieldConfig?.field?.type !== "photo") continue;
    const photos = next[key]?.value;
    const built = buildPhotoVariantValue(fieldConfig, photos);
    if (!built) {
      if (next[key] != null) {
        delete next[key];
        changed = true;
      }
      continue;
    }
    if (JSON.stringify(next[key]) !== JSON.stringify(built)) {
      next[key] = built;
      changed = true;
    }
  }
  return changed ? next : variant;
}

/** Deep-clones variants and applies size-based option overrides. For catalog.rules surcharges, use getVariantsBySizeWithRules in quote flows. */
export function getVariantsBySize(variants, item) {
  const tmp = JSON.parse(JSON.stringify(variants || {}));
  if (!item.variant?.size) {
    return tmp;
  }
  for (const [key, value] of Object.entries(tmp)) {
    if (key === "size") {
      continue;
    }
    if (value.field.type === "select" && item.variant.size) {
      tmp[key].field.options = getOptionListByOverride(value.field.options, {
        size: item.variant.size.name,
      });
      if (tmp[key].field.options.length === 0) {
        delete tmp[key];
      }
    }
  }
  applyPhotoPricingFromSize(tmp, item.variant);
  return tmp;
}

/**
 * When the selected size changes, drop variant selections that are tied to the old size:
 * 1) Select fields whose chosen option had `override` (size-based pricing) in `fields`.
 * 2) Fields listed in `settings.field` on the old or new size option (e.g. flavor), plus
 *    `filling` when `flavor` is cleared.
 *
 * `fields` should be the variant field map for the item **before** the size change (e.g.
 * `getVariantsBySize(variants, item)` with `item` still holding the previous size).
 *
 * @param {object} nextVariant - incoming variant from the UI (includes new size)
 * @param {object} [previousVariant] - variant before the change
 * @param {object} [fields] - field config map (same shape as getVariantsBySize output)
 * @returns {object} shallow copy of nextVariant with cleared keys removed
 */
export function pruneVariantFieldsOnSizeChange(nextVariant, previousVariant, fields) {
  if (!nextVariant || !previousVariant || !fields) {
    return nextVariant || {};
  }
  if (
    !previousVariant.size?.name ||
    !nextVariant.size?.name ||
    previousVariant.size.name === nextVariant.size.name
  ) {
    return nextVariant;
  }

  const variant = { ...nextVariant };

  Object.entries(previousVariant).forEach(([variantKey, variantValue]) => {
    if (variantKey === "size") {
      return;
    }
    if (variantValue?.name && fields[variantKey]?.field?.options) {
      const originalOption = fields[variantKey].field.options.find(
        (opt) => opt.name === variantValue.name
      );
      if (originalOption?.override) {
        delete variant[variantKey];
      }
    }
  });

  if (fields.size?.field?.options) {
    const oldSizeOption = fields.size.field.options.find(
      (opt) => opt.name === previousVariant.size?.name
    );
    const newSizeOption = fields.size.field.options.find(
      (opt) => opt.name === nextVariant.size?.name
    );
    if (oldSizeOption?.settings || newSizeOption?.settings) {
      const fieldsToClear = new Set([
        ...Object.keys(oldSizeOption?.settings?.field || {}),
        ...Object.keys(newSizeOption?.settings?.field || {}),
      ]);
      fieldsToClear.forEach((fieldKey) => {
        if (variant[fieldKey]) {
          delete variant[fieldKey];
          if (fieldKey === "flavor") {
            delete variant.filling;
          }
        }
      });
    }
  }

  return variant;
}

export function getVariantFieldRank(o) {
  let rank = o.field?.rank || 0;

  if (rank === 0) {
    if (o.key) {
      if (o.key === "size") {
        rank = 20;
      } else if (o.key === "flavor") {
        rank = 19;
      } else if (o.key === "filling") {
        rank = 18;
      } else if (o.key === "icing") {
        rank = 17;
      } else if (o.key === "photo") {
        rank = 6;
      } else if (o.key === "inscription") {
        rank = 2;
      } else if (o.key === "notes") {
        rank = 1;
      } else {
        rank = 5;
      }
    }
  }

  return rank;
}

export function sortVariantFieldsByRank(o1, o2) {
  let rslt = getVariantFieldRank(o2) - getVariantFieldRank(o1);
  if (rslt === 0) {
    rslt = o1.title > o2.title ? 1 : -1;
  }
  return rslt;
}

export function getReferrer(utm) {
  let referrer = "";
  if (utm) {
    if (utm.utmc) {
      referrer = `${utm.utms}:${utm.utmc}`;
    } else {
      referrer = utm.utms;
    }
  }
  return referrer;
}

export function isInventoryControlEnabled(siteId) {
  return false;
}

export function isCashAdvanceOptionAvailable(siteId) {
  return (
    siteId === "6WOVOXtelMJ1ZwfEWs17" ||
    siteId === "6VRiUORmuZ7IORV5CONz" ||
    siteId === "-MROCJAn2bWELBXJnKQ-" ||
    siteId === "djPWIkh9e88MTXm4jYhb"
  );
}

export function isItemInStock(siteId, item) {
  if (isInventoryControlEnabled(siteId)) {
    const currentStock = +item.stock || 0;
    return currentStock > 0;
  } else {
    return true;
  }
}

export function getOptimizedCloudinaryUrl(originalUrl, transformations = "f_auto,q_auto") {
  if (!originalUrl || typeof originalUrl !== "string" || !originalUrl.includes("cloudinary.com")) {
    return originalUrl;
  }

  // Define the segment where transformations are typically inserted
  const uploadSegment = "/upload/";

  // Check if the URL is a standard Cloudinary URL that can be transformed
  if (originalUrl.includes(uploadSegment)) {
    // 1. Find the index right after '/upload/'
    const insertIndex = originalUrl.indexOf(uploadSegment) + uploadSegment.length;

    // 2. Insert the transformations string (e.g., 'f_auto,q_auto/')
    const optimizedUrl =
      originalUrl.slice(0, insertIndex) +
      transformations +
      "/" + // Add a trailing slash after the transformations
      originalUrl.slice(insertIndex);

    return optimizedUrl;
  }

  // Return the original URL if the structure is unexpected
  return originalUrl;
}

export function isMultiModalPaymentAvailable(account, order) {
  console.log("isMultiModalPaymentAvailable", isPOSBakery(account));
  return (
    isPOSBakery(account) &&
    order.useTxnLedger === true &&
    order.src === "direct" &&
    +order.payment.due > 0 &&
    order.status !== "canceled"
  );
}
